import React from 'react';
import {
  Stack,
  TextInput,
  SegmentedControl,
  ActionIcon,
  Group,
  Text
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

type StaticOption = {
  value: string;
  label: string;
};

type ReadmodelSource = {
  readmodelName?: string;
  select?: string[];
};

type ExternalUrlSource = {
  url?: string;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST';
  body?: string;
};

type ExposeConfig = {
  kind?: string;
  label?: string;
  value?: string;
};

type OptionsValue = {
  readmodel?: ReadmodelSource;
  static?: StaticOption[];
  externalUrl?: ExternalUrlSource;
  expose?: ExposeConfig;
};

export type OptionsEditorProps = {
  options: Record<string, OptionsValue>;
  onChange: ( options: Record<string, OptionsValue> ) => void;
};

const INPUT_STYLES = {
  label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
  input: {
    background: 'var(--drawer-input-bg)',
    borderColor: 'var(--drawer-input-border)',
    color: 'var(--drawer-text)'
  }
} as const;

const SOURCES = [
  { label: 'Readmodel', value: 'readmodel' },
  { label: 'Static', value: 'static' },
  { label: 'External URL', value: 'externalUrl' }
] as const;

type SourceType = 'readmodel' | 'static' | 'externalUrl';

/**
 * Determine active source from options object.
 */
function detectSource( optionValue: OptionsValue ): SourceType {
  if ( optionValue.static && optionValue.static.length > 0 ) return 'static';
  if ( optionValue.externalUrl?.url ) return 'externalUrl';
  return 'readmodel';
}

/**
 * Editor for select/multiSelect options configuration.
 * Supports readmodel, static, and externalUrl sources with expose config.
 */
export function OptionsEditor({ options, onChange }: OptionsEditorProps ) {
  const optionKeys = Object.keys( options );

  if ( optionKeys.length === 0 ) {
    return null;
  }

  const handleOptionChange = ( key: string, value: OptionsValue ) => {
    onChange({ ...options, [key]: value });
  };

  return (
    <Stack gap="md">
      {optionKeys.map(( key ) => {
        const optionValue = options[key] ?? {};
        const source = detectSource( optionValue );

        return (
          <SingleOptionEditor
            key={key}
            optionKey={key}
            value={optionValue}
            source={source}
            onChange={( val ) => handleOptionChange( key, val )}
          />
        );
      })}
    </Stack>
  );
}

type SingleOptionEditorProps = {
  optionKey: string;
  value: OptionsValue;
  source: SourceType;
  onChange: ( value: OptionsValue ) => void;
};

function SingleOptionEditor({ optionKey, value, source, onChange }: SingleOptionEditorProps ) {
  const handleSourceChange = ( newSource: string ) => {
    const nextValue: OptionsValue = { expose: value.expose };
    if ( newSource === 'readmodel' ) {
      nextValue.readmodel = value.readmodel ?? { readmodelName: '' };
    } else if ( newSource === 'static' ) {
      nextValue.static = value.static ?? [];
    } else if ( newSource === 'externalUrl' ) {
      nextValue.externalUrl = value.externalUrl ?? { url: '', method: 'GET' };
    }
    onChange( nextValue );
  };

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
        {optionKey}
      </Text>

      <SegmentedControl
        value={source}
        onChange={handleSourceChange}
        data={SOURCES as unknown as { label: string; value: string }[]}
        size="xs"
        fullWidth
      />

      {source === 'readmodel' && (
        <ReadmodelSourceEditor
          readmodel={value.readmodel ?? {}}
          onChange={( rm ) => onChange({ ...value, readmodel: rm, static: undefined, externalUrl: undefined })}
        />
      )}

      {source === 'static' && (
        <StaticSourceEditor
          items={value.static ?? []}
          onChange={( items ) => onChange({ ...value, static: items, readmodel: undefined, externalUrl: undefined })}
        />
      )}

      {source === 'externalUrl' && (
        <ExternalUrlSourceEditor
          externalUrl={value.externalUrl ?? { url: '', method: 'GET' }}
          onChange={( eu ) => onChange({ ...value, externalUrl: eu, readmodel: undefined, static: undefined })}
        />
      )}

      <ExposeEditor
        expose={value.expose ?? {}}
        onChange={( exp ) => onChange({ ...value, expose: exp })}
      />
    </Stack>
  );
}

type ReadmodelSourceEditorProps = {
  readmodel: ReadmodelSource;
  onChange: ( readmodel: ReadmodelSource ) => void;
};

function ReadmodelSourceEditor({ readmodel, onChange }: ReadmodelSourceEditorProps ) {
  return (
    <Stack gap="xs">
      <TextInput
        label="Readmodel Name"
        value={readmodel.readmodelName ?? ''}
        onChange={( e ) => onChange({ ...readmodel, readmodelName: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
      />
      <TextInput
        label="Select Fields (comma-separated)"
        value={( readmodel.select ?? []).join( ', ' )}
        onChange={( e ) => {
          const fields = e.currentTarget.value
            .split( ',' )
            .map(( s ) => s.trim())
            .filter( Boolean );
          onChange({ ...readmodel, select: fields });
        }}
        size="xs"
        styles={INPUT_STYLES}
        placeholder="field1, field2"
      />
    </Stack>
  );
}

type StaticSourceEditorProps = {
  items: StaticOption[];
  onChange: ( items: StaticOption[]) => void;
};

function StaticSourceEditor({ items, onChange }: StaticSourceEditorProps ) {
  const handleAdd = () => {
    onChange([ ...items, { value: '', label: '' }]);
  };

  const handleRemove = ( index: number ) => {
    onChange( items.filter(( _, i ) => i !== index ));
  };

  const handleItemChange = ( index: number, field: 'value' | 'label', newVal: string ) => {
    const next = items.map(( item, i ) =>
      i === index ? { ...item, [field]: newVal } : item );
    onChange( next );
  };

  return (
    <Stack gap="xs">
      {items.map(( item, index ) => (
        <Group key={`${item.value}-${item.label}-${index}`} gap="xs" align="flex-end" wrap="nowrap">
          <TextInput
            label={index === 0 ? 'Value' : undefined}
            value={item.value}
            onChange={( e ) => handleItemChange( index, 'value', e.currentTarget.value )}
            size="xs"
            styles={INPUT_STYLES}
            style={{ flex: 1 }}
          />
          <TextInput
            label={index === 0 ? 'Label' : undefined}
            value={item.label}
            onChange={( e ) => handleItemChange( index, 'label', e.currentTarget.value )}
            size="xs"
            styles={INPUT_STYLES}
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => handleRemove( index )}
            style={{ marginBottom: index === 0 ? 0 : undefined }}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}

      <ActionIcon
        variant="light"
        color="gray"
        size="sm"
        onClick={handleAdd}
      >
        <IconPlus size={14} />
      </ActionIcon>
    </Stack>
  );
}

type ExternalUrlSourceEditorProps = {
  externalUrl: ExternalUrlSource;
  onChange: ( externalUrl: ExternalUrlSource ) => void;
};

function ExternalUrlSourceEditor({ externalUrl, onChange }: ExternalUrlSourceEditorProps ) {
  return (
    <Stack gap="xs">
      <TextInput
        label="URL"
        value={externalUrl.url ?? ''}
        onChange={( e ) => onChange({ ...externalUrl, url: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
        placeholder="https://api.example.com/options"
      />
      <SegmentedControl
        value={externalUrl.method ?? 'GET'}
        onChange={( val ) => onChange({ ...externalUrl, method: val as 'GET' | 'POST' })}
        data={[ 'GET', 'POST' ]}
        size="xs"
      />
      {externalUrl.method === 'POST' && (
        <TextInput
          label="Body"
          value={externalUrl.body ?? ''}
          onChange={( e ) => onChange({ ...externalUrl, body: e.currentTarget.value })}
          size="xs"
          styles={INPUT_STYLES}
          placeholder='{"key": "value"}'
        />
      )}
    </Stack>
  );
}

type ExposeEditorProps = {
  expose: ExposeConfig;
  onChange: ( expose: ExposeConfig ) => void;
};

function ExposeEditor({ expose, onChange }: ExposeEditorProps ) {
  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" style={{ marginTop: 8 }}>
        Expose Config
      </Text>
      <TextInput
        label="Kind"
        value={expose.kind ?? ''}
        onChange={( e ) => onChange({ ...expose, kind: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
      />
      <TextInput
        label="Label Template"
        value={expose.label ?? ''}
        onChange={( e ) => onChange({ ...expose, label: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
        placeholder="{{name}} - {{code}}"
      />
      <TextInput
        label="Value Template"
        value={expose.value ?? ''}
        onChange={( e ) => onChange({ ...expose, value: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
        placeholder="{{id}}"
      />
    </Stack>
  );
}
